import { z } from 'zod';
import { PERMISSIONS } from '../auth/permissions';
import { idParamSchema, uuidSchema } from './common';

const MAX_NAME_LENGTH = 100;
const MAX_HUBS_PER_GRANT = 50;
const MAX_HUB_TAGS_PER_GRANT = 20;
const MAX_GRANTS = 20;

const permissionEnum = z.enum(PERMISSIONS).refine(
  (p) => p !== 'platform:admin',
  { message: 'platform:admin cannot be delegated via token creation' },
);

const grantScopeSchema = z.object({
  // Required: every token grant must declare its org tenancy. The handler
  // also strict-checks this as defense-in-depth, but failing at the Zod
  // boundary keeps the generated client type honest (org_id: string).
  org_id: z.string().min(1),
  hubs: z.array(z.string().min(1)).min(1, 'Each grant must have scope.hubs as a non-empty array').max(MAX_HUBS_PER_GRANT),
  hub_tags: z.array(z.string().min(1)).min(1, 'scope.hub_tags must be a non-empty array if provided').max(MAX_HUB_TAGS_PER_GRANT).optional(),
  environments: z.array(z.enum(['production', 'preview'])).min(1, 'scope.environments must be a non-empty array if provided').optional(),
}).strict();

const grantSchema = z.object({
  scope: grantScopeSchema,
  permissions: z.array(permissionEnum).min(1, 'Each grant must have permissions as a non-empty array'),
}).strict();

export const createTokenBody = z.object({
  name: z.string().trim().min(1, 'name is required').max(MAX_NAME_LENGTH, `name must be ${MAX_NAME_LENGTH} characters or fewer`),
  grants: z.array(grantSchema).min(1, 'grants must be a non-empty array').max(MAX_GRANTS),
  expires_at: z.string().datetime({ message: 'expires_at must be a valid ISO 8601 date' }).refine(
    (s) => new Date(s).getTime() > Date.now(),
    { message: 'expires_at must be in the future' },
  ).optional(),
});

export const tokenIdParam = idParamSchema;

// Governance route param. NOT idParamSchema / setup's orgIdParam (both key
// `id`); the route segment is `:orgId`, so the param bag must carry `orgId`.
export const tokenOrgIdParam = z.object({ orgId: uuidSchema });

// --- Response contracts ---------------------------------------------------
// Stored/returned grants always carry a concrete scope.org_id (the create
// handler strictly requires it before persisting).
const tokenGrantScopeSchema = z.object({
  org_id: z.string().min(1),
  hubs: z.array(z.string().min(1)).min(1),
  hub_tags: z.array(z.string().min(1)).min(1).optional(),
  environments: z.array(z.enum(['production', 'preview'])).min(1).optional(),
});

export const tokenGrantSchema = z.object({
  scope: tokenGrantScopeSchema,
  permissions: z.array(z.enum(PERMISSIONS)).min(1),
});

export const tokenMetadataSchema = z.object({
  token_id: z.string(),
  name: z.string(),
  token_prefix: z.string(),
  grants: z.array(tokenGrantSchema),
  created_at: z.string(),
  expires_at: z.string().optional(),
  last_used_at: z.string().optional(),
});

export const listTokensResponse = z.object({
  data: z.array(tokenMetadataSchema),
});

export const createTokenResponse = z.object({
  data: z.object({
    token: z.string(),
    token_id: z.string(),
    name: z.string(),
    expires_at: z.string().optional(),
  }),
});

export const orgTokenSummarySchema = z.object({
  token_id: z.string(),
  name: z.string(),
  token_prefix: z.string(),
  owner_user_id: z.string(),
  created_at: z.string(),
  expires_at: z.string().optional(),
  last_used_at: z.string().optional(),
  grants_for_org: z.array(tokenGrantSchema),
});

export const listOrgTokensResponse = z.object({
  data: z.array(orgTokenSummarySchema),
});

export type CreateTokenBody = z.infer<typeof createTokenBody>;
export type TokenIdParam = z.infer<typeof tokenIdParam>;
export type TokenOrgIdParam = z.infer<typeof tokenOrgIdParam>;
export type TokenGrant = z.infer<typeof tokenGrantSchema>;
export type TokenMetadata = z.infer<typeof tokenMetadataSchema>;
export type OrgTokenSummary = z.infer<typeof orgTokenSummarySchema>;

export const TOKEN_LIMITS = {
  MAX_NAME_LENGTH,
  MAX_HUBS_PER_GRANT,
  MAX_HUB_TAGS_PER_GRANT,
  MAX_GRANTS,
} as const;
